import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, Download, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatUSD } from "../lib/api";

const STATUSES = ["Pending", "Contacted", "Assay Verified", "Contract Issued", "Completed", "Cancelled"];

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3EFE6] px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm bg-white border border-[#E6E0D4] p-9"
      >
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={34} />
          <div>
            <p className="font-serif text-lg tracking-[0.14em] text-[#1A1816]">PACIFIC GOLD</p>
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#8C6D27]">Orders desk</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="admin-email" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Email</Label>
            <Input id="admin-email" data-testid="admin-login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none bg-[#FAF8F5] border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Password</Label>
            <Input id="admin-password" data-testid="admin-login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none bg-[#FAF8F5] border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-xs text-red-700">{error}</p>}
          <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full rounded-none h-12 bg-[#141312] text-[#FAF8F5] hover:bg-[#8C6D27] text-[12px] tracking-[0.16em] uppercase">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <a href="/" className="mt-6 block text-center text-[11px] tracking-[0.2em] uppercase text-[#6E6A63] hover:text-[#8C6D27] transition-colors">
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  const loadOrders = () =>
    api.get("/admin/orders").then((res) => setOrders(res.data.orders || [])).catch(() => {});

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        setUser(res.data);
        loadOrders().finally(() => setLoaded(true));
      })
      .catch(() => setUser(null));
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((os) => os.map((o) => (o.order_id === orderId ? { ...o, status } : o)));
    } catch (err) {
      toast.error("Could not update status", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const filtered = useMemo(
    () => (statusFilter === "all" ? orders : orders.filter((o) => o.status === statusFilter)),
    [orders, statusFilter]
  );

  const stats = useMemo(() => {
    const pipeline = orders.reduce((s, o) => s + (o.estimated_value_usd || 0), 0);
    const kg = orders.reduce((s, o) => s + (o.weight_grams || 0), 0) / 1000;
    return {
      total: orders.length,
      pending: orders.filter((o) => o.status === "Pending").length,
      kg: kg.toFixed(2),
      pipeline,
    };
  }, [orders]);

  const exportCsv = () => {
    const header = ["Reference", "Date", "Name", "Company", "Email", "Phone", "Product", "Qty", "Weight (g)", "Est. value (USD)", "Delivery", "Status", "Message"];
    const rows = filtered.map((o) => [o.order_id, o.created_at, o.name, o.company || "", o.email, o.phone, o.product_name, o.quantity, o.weight_grams, o.estimated_value_usd, o.delivery_preference, o.status, (o.message || "").replace(/\n/g, " ")]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "pacific-gold-orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-[#F3EFE6]" />;
  }
  if (user === null) return <LoginForm onSuccess={(u) => { setUser(u); loadOrders().finally(() => setLoaded(true)); }} />;

  return (
    <div className="min-h-screen bg-[#F3EFE6]" data-testid="admin-dashboard">
      <header className="bg-[#141312] text-[#FAF8F5]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-serif tracking-[0.14em]">PACIFIC GOLD</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2.5 py-1 ml-2">Orders desk</span>
          </div>
          <Button data-testid="admin-logout" onClick={logout} variant="ghost" className="rounded-none text-[#FAF8F5]/80 hover:text-[#FAF8F5] hover:bg-[#FAF8F5]/10 text-[11px] tracking-[0.18em] uppercase">
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            ["Order requests", stats.total, "admin-stat-total"],
            ["Pending review", stats.pending, "admin-stat-pending"],
            ["Gold requested", `${stats.kg} kg`, "admin-stat-kg"],
            ["Pipeline value", formatUSD(stats.pipeline), "admin-stat-pipeline"],
          ].map(([label, value, id]) => (
            <div key={id} data-testid={id} className="bg-white border border-[#E6E0D4] p-6">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6E6A63]">{label}</p>
              <p className="font-mono text-2xl lg:text-3xl text-[#1A1816] mt-3">{value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E6E0D4]">
          <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b border-[#E6E0D4]">
            <h2 className="font-serif text-xl text-[#1A1816] flex items-center gap-3">
              <ShieldCheck size={17} className="text-[#C5A059]" /> Order requests
            </h2>
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="rounded-none border-[#E6E0D4] w-[180px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="admin-export-csv" onClick={exportCsv} variant="outline" className="rounded-none border-[#E6E0D4] text-[11px] tracking-[0.16em] uppercase hover:border-[#C5A059]">
                <Download size={14} className="mr-2" /> CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="admin-orders-table" className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#6E6A63] border-b border-[#E6E0D4]">
                  {["Ref", "Date", "Buyer", "Order", "Value", "Delivery", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.order_id} data-testid={`admin-order-row-${o.order_id}`} className="border-b border-[#F3EFE6] hover:bg-[#FAF8F5]/60 transition-colors">
                    <td className="px-4 py-4 font-mono text-xs text-[#8C6D27]">{o.order_id}</td>
                    <td className="px-4 py-4 font-mono text-xs text-[#6E6A63]">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-4">
                      <p className="text-[#1A1816]">{o.name}{o.company ? ` — ${o.company}` : ""}</p>
                      <p className="text-xs text-[#6E6A63] font-mono mt-0.5">{o.email} · {o.phone}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-[#1A1816]">{o.product_name}</p>
                      <p className="text-xs text-[#6E6A63] font-mono mt-0.5">×{o.quantity} · {(o.weight_grams / 1000).toLocaleString()} kg</p>
                    </td>
                    <td className="px-4 py-4 font-mono text-[#1A1816]">{formatUSD(o.estimated_value_usd)}</td>
                    <td className="px-4 py-4 text-xs text-[#6E6A63]">{o.delivery_preference}</td>
                    <td className="px-4 py-4">
                      <Select value={o.status} onValueChange={(v) => updateStatus(o.order_id, v)}>
                        <SelectTrigger data-testid={`admin-order-status-${o.order_id}`} className={`rounded-none border h-8 text-xs w-[150px] ${o.status === "Pending" ? "border-amber-700/40 text-amber-800" : o.status === "Completed" ? "border-green-700/40 text-green-700" : o.status === "Cancelled" ? "border-red-700/30 text-red-700" : "border-[#E6E0D4] text-[#1A1816]"}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {loaded && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center text-sm text-[#6E6A63]">
                      No order requests {statusFilter !== "all" ? "with this status " : ""}yet. New wholesale requests appear here instantly.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
